import { PALETTE_AURORA } from '@shared/builtinPulseMaps/palettes';
import { JUMP_VARIANT_TUNING, type JumpChart } from '@shared/jumpChart';
import {
  createJumpCharGrid,
  packJumpGrid,
  paintCeiling,
  paintFloor,
  setJumpCell,
  JUMP_CELL_BEATS,
  JUMP_CELL_PX,
  JUMP_GRID_ROWS,
  JUMP_GROUND_ROWS,
  JUMP_TILE,
  type JumpGridHint,
} from '@shared/jumpGrid';
import type { ChartAudio } from '@shared/chart';

/**
 * Hand-authored teaching grid for `/jump/tutorial` — the first proof the tile
 * format is hand-paintable. It drills the two jump heights then the two new
 * mechanics, across five contrasting gates, each with an on-canvas coaching pill:
 *
 *   beat  4  jump 1 — a LOW spike (1 cell): a short tap clears it; its glowing
 *                     perfect-zone band is introduced here too ("TAP")
 *   beat  8  jump 2 — a TALL spike wall (3 cells): only a held full jump clears ("HOLD!")
 *   beat 12  jump 3 — a LOW spike, drilling the PERFECT ZONE explicitly: a short
 *                     tap grazes the band for a perfect, a full jump overshoots ("TAP = PERFECT")
 *   beat 16  jump 4 — a low spike under a CEILING corridor: a full jump smacks
 *                     the overhang, only a short tap fits beneath ("SHORT TAP!")
 *   beat 20  jump 5 — a PIT in the floor: walk off and you fall — leap it ("JUMP!")
 *   beat 20-26  brief tail before the "you've got it" card
 *
 * Gates sit 4 beats apart — well above the ~0.9-beat long air-time — so a
 * first-timer always lands before the next press. Tuning copies the Normal
 * variant so clearing the tutorial steps into the easiest tier at the same feel.
 * Audio reuses the shared `my-heart.mp3` tutorial track at 100 BPM.
 */
const TUTORIAL_BPM = 100;

/** Whole-chart length in beats (the grid rounds up to a column multiple). */
const TUTORIAL_TOTAL_BEATS = 26;

/** Baseline floor's top row — spikes stand on the cell just above it. */
const FLOOR_TOP_ROW = JUMP_GRID_ROWS - JUMP_GROUND_ROWS;

/** LOW spike: 1 cell (36 units) — comfortably under the short hop's ~75-unit apex. */
const LOW_SPIKE_CELLS = 1;

/** TALL spike wall: 3 cells (108 units) — above the short reach, under the long (~150). */
const TALL_SPIKE_CELLS = 3;

/**
 * Corridor ceiling depth (rows from the top). 8 rows → a lethal underside at
 * Y 288, between the long jump's apex (cube top ≈ 252, cracks it) and the short
 * hop's (cube top ≈ 327, fits beneath) — so it forces the short tap.
 */
const CORRIDOR_CEIL_ROWS = 8;

/** Pit width in columns (3 = 0.375 beat) — either jump height carries across. */
const PIT_COLS = 3;

function beatToCol(beat: number): number {
  return Math.round(beat / JUMP_CELL_BEATS);
}

export function buildTutorialJumpChart(): JumpChart {
  const tuning = JUMP_VARIANT_TUNING.normal;
  const audio: ChartAudio = {
    src: '/audio/circles/my-heart.mp3',
    bpm: TUTORIAL_BPM,
    offsetMs: 0,
    durationSec: 60,
    attribution:
      'Tutorial uses "Heart Afire" by Defqwop (feat. Strix) [NCS Release] - https://ncs.io/HeartAfire',
    onsets: [],
  };

  const cols = Math.ceil(TUTORIAL_TOTAL_BEATS / JUMP_CELL_BEATS);
  const charGrid = createJumpCharGrid(JUMP_GRID_ROWS, cols);
  paintFloor(charGrid, 0, cols, JUMP_GROUND_ROWS);

  const hints: JumpGridHint[] = [];
  const placeSpike = (beat: number, cells: number, text: string): void => {
    const col = beatToCol(beat);
    for (let r = 0; r < cells; r += 1) {
      setJumpCell(charGrid, FLOOR_TOP_ROW - 1 - r, col, JUMP_TILE.spikeUp);
    }
    hints.push({ col, text });
  };

  // Gate 1 — low spike: any tap clears.
  placeSpike(4, LOW_SPIKE_CELLS, 'TAP');
  // Gate 2 — tall spike wall: only a held full jump reaches over it.
  placeSpike(8, TALL_SPIKE_CELLS, 'HOLD!');
  // Gate 3 — low spike again, now teaching the perfect zone: a short tap grazes
  // the glowing band for a PERFECT, while a full jump overshoots it (still a GOOD).
  placeSpike(12, LOW_SPIKE_CELLS, 'TAP = PERFECT');
  // Gate 4 — short-jump corridor: a low spike forces a jump, the ceiling forbids
  // a full one, so only a short tap threads beneath.
  const corridorCol = beatToCol(16);
  paintCeiling(charGrid, corridorCol - 1, corridorCol + 2, CORRIDOR_CEIL_ROWS);
  placeSpike(16, LOW_SPIKE_CELLS, 'SHORT TAP!');
  // Gate 5 — a pit: walk off and you fall, so leap the gap.
  const pitStartCol = beatToCol(20);
  paintFloor(charGrid, pitStartCol, pitStartCol + PIT_COLS, 0);
  hints.push({ col: pitStartCol, text: 'JUMP!' });

  const grid = packJumpGrid(charGrid, JUMP_CELL_BEATS, JUMP_CELL_PX, hints);

  return {
    mode: 'jump',
    id: 'tutorial-jump',
    slug: 'tutorial-jump',
    title: 'How to play (Jump)',
    description: 'Quick teaching chart: tap for a short hop, hold for a big jump.',
    bpm: TUTORIAL_BPM,
    difficulty: 1,
    variant: 'normal',
    totalBeats: cols * JUMP_CELL_BEATS,
    palette: PALETTE_AURORA,
    audio,
    gravity: tuning.gravity,
    jumpVelocity: tuning.jumpVelocity,
    shortJumpVelocity: tuning.shortJumpVelocity,
    playerSize: tuning.playerSize,
    grid,
  };
}

/**
 * Overlay instructions for the tutorial page, each anchored to a beat range.
 * The page shows the most recent overlay whose `fromBeat <= currentBeat <
 * untilBeat`. The just-in-time "TAP / HOLD / SHORT / JUMP" timing lives in the
 * on-canvas pills above each gate; these cards carry the higher-level lesson.
 */
export interface JumpTutorialOverlay {
  fromBeat: number;
  untilBeat: number;
  title: string;
  body: string;
}

/**
 * Returns the overlay sequence with input wording adapted to the device. Touch
 * devices get "tap" copy; keyboard devices see Space as the primary binding
 * (Jump shares the Pulse/Waves key set so a multi-mode player has one button).
 */
export function getJumpTutorialOverlays(isTouch: boolean): JumpTutorialOverlay[] {
  // Capitalised variants for sentence starts; lower-case for mid-sentence use.
  const tapVerbCap = isTouch ? 'A quick tap' : 'A quick tap of Space';
  const holdVerb = isTouch ? 'hold' : 'hold Space';
  const holdVerbCap = isTouch ? 'Hold' : 'Hold Space';
  return [
    {
      fromBeat: -8,
      untilBeat: 2,
      title: 'Two jump heights',
      body: `Your cube has a small hop and a big jump. ${tapVerbCap} gives a short hop; ${holdVerb} for a full jump. You'll need both.`,
    },
    {
      fromBeat: 2,
      untilBeat: 6,
      title: 'Quick tap',
      body: 'This low spike is easy — a short tap pops you right over it. Aim your cube through the glowing green band for a PERFECT.',
    },
    {
      fromBeat: 6,
      untilBeat: 10,
      title: 'Hold for height',
      body: `This spike wall is tall — a quick tap won't reach. ${holdVerbCap} to launch the full big jump and sail through its zone.`,
    },
    {
      fromBeat: 10,
      untilBeat: 14,
      title: 'Hit the perfect zone',
      body: 'Each spike floats a green band — the perfect window. Match the jump to the spike and time it so your cube grazes the green: PERFECT. Clear it any other way and it still scores a GOOD.',
    },
    {
      fromBeat: 14,
      untilBeat: 18,
      title: 'Duck the ceiling',
      body: 'A full jump cracks the ceiling above! Use a SHORT tap to stay low and slip under it.',
    },
    {
      fromBeat: 18,
      untilBeat: 23,
      title: 'Leap the gap',
      body: 'A hole in the floor! Walk into it and you fall. Jump across — either height carries you over.',
    },
    {
      fromBeat: 23,
      untilBeat: Number.POSITIVE_INFINITY,
      title: "You\u2019re ready",
      body: 'That\u2019s both heights plus ceilings and pits. Pick a real track from the home screen and put them to work.',
    },
  ];
}
